import { useEffect, useSyncExternalStore, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { guideProgressStore, GuideStep } from '@/lib/guideProgressStore';

export type { GuideStep };

export interface GuideProgress {
  steps: GuideStep[];
  completedSteps: string[];
  isLoading: boolean;
  isComplete: boolean;
  completedCount: number;
  totalCount: number;
  progressPercent: number;
  markStepComplete: (stepKey: string) => Promise<void>;
  refetch: () => Promise<void>;
}

export function useGuideProgress(): GuideProgress {
  const { user, company } = useAuth();

  const snapshot = useSyncExternalStore(
    guideProgressStore.subscribe,
    guideProgressStore.getSnapshot,
    guideProgressStore.getSnapshot
  );

  useEffect(() => {
    guideProgressStore.init(user, company);
  }, [user?.id, company?.id]);

  const markStepComplete = useCallback(
    (stepKey: string) => guideProgressStore.markStepComplete(stepKey),
    []
  );

  const refetch = useCallback(() => guideProgressStore.refetch(), []);

  const { steps, completedSteps, isLoading } = snapshot;
  const totalCount = steps.length;
  const completedCount = completedSteps.length;
  const isComplete = totalCount > 0 && completedCount >= totalCount;
  const progressPercent =
    totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return {
    steps,
    completedSteps,
    isLoading,
    isComplete,
    completedCount,
    totalCount,
    progressPercent,
    markStepComplete,
    refetch,
  };
}
